import request from 'axios';
import { reset } from 'redux-form';
import idx from 'idx';
import queryString from 'query-string';
import rangeRight from 'lodash/rangeRight';
import sum from 'lodash/sum';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import union from 'lodash/union';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { showBanner, TYPE_SUCCESS } from 'common/ducks/banner';
import { setBannerError } from 'error';
import {
  AUTOMATIONS_RESOURCE_NAME, GROUPS_RESOURCE_NAME, AUTOMATION_FORM_NAME
} from 'email_automations/constants';
import { getAPIQueryString } from 'common/utils';
import { savePageCount } from 'common/pager/ducks';
import {
  sendTestEmail as sendTestEmailCommon, saveEntryConditions, fromQueryPartToBreakdown
} from 'email_automations/ducks/common';

/* ACTIONS
================================================================================================ */
const FETCH_TOTALS = 'crowdspring/bumblebee/FETCH_TOTALS';
const FETCH_TOTALS_SUCCESS = 'crowdspring/bumblebee/FETCH_TOTALS_SUCCESS';

const FETCH_AUTOMATIONS = 'crowdspring/bumblebee/FETCH_AUTOMATIONS';
const FETCH_AUTOMATIONS_REQUEST = 'crowdspring/bumblebee/FETCH_AUTOMATIONS_REQUEST';
const FETCH_AUTOMATIONS_SUCCESS = 'crowdspring/bumblebee/FETCH_AUTOMATIONS_SUCCESS';

const FETCH_GROUP = 'crowdspring/bumblebee/FETCH_GROUP';
const FETCH_GROUP_REQUEST = 'crowdspring/bumblebee/FETCH_GROUP_REQUEST';
const FETCH_GROUP_SUCCESS = 'crowdspring/bumblebee/FETCH_GROUP_SUCCESS';

const FETCH_METRICS = 'crowdspring/bumblebee/FETCH_METRICS';
const FETCH_METRICS_SUCCESS = 'crowdspring/bumblebee/FETCH_METRICS_SUCCESS';

const PAUSE_AUTOMATION = 'crowdspring/bumblebee/PAUSE_AUTOMATION';
const PAUSE_AUTOMATION_SUCCESS = 'crowdspring/bumblebee/PAUSE_AUTOMATION_SUCCESS';

const RESUME_AUTOMATION = 'crowdspring/bumblebee/RESUME_AUTOMATION';
const RESUME_AUTOMATION_SUCCESS = 'crowdspring/bumblebee/RESUME_AUTOMATION_SUCCESS';

const SET_LIVE_AUTOMATION = 'crowdspring/bumblebee/SET_LIVE_AUTOMATION';
const SET_LIVE_AUTOMATION_SUCCESS = 'crowdspring/bumblebee/SET_LIVE_AUTOMATION_SUCCESS';

const END_AUTOMATION = 'crowdspring/bumblebee/END_AUTOMATION';
const END_AUTOMATION_SUCCESS = 'crowdspring/bumblebee/END_AUTOMATION_SUCCESS';

const FETCH_GROUPS = 'crowdspring/bumblebee/FETCH_GROUPS';
const FETCH_GROUPS_REQUEST = 'crowdspring/bumblebee/FETCH_GROUPS_REQUEST';
const FETCH_GROUPS_SUCCESS = 'crowdspring/bumblebee/FETCH_GROUPS_SUCCESS';

const PAUSE_GROUP = 'crowdspring/bumblebee/PAUSE_GROUP';
const PAUSE_GROUP_SUCCESS = 'crowdspring/bumblebee/PAUSE_GROUP_SUCCESS';

const RESUME_GROUP = 'crowdspring/bumblebee/RESUME_GROUP';
const RESUME_GROUP_SUCCESS = 'crowdspring/bumblebee/RESUME_GROUP_SUCCESS';

const END_GROUP = 'crowdspring/bumblebee/END_GROUP';
const END_GROUP_SUCCESS = 'crowdspring/bumblebee/END_GROUP_SUCCESS';

const SAVE_GROUP = 'crowdspring/bumblebee/SAVE_GROUP';

const FETCH_AUTOMATION = 'crowdspring/bumblebee/FETCH_AUTOMATION';
const FETCH_AUTOMATION_REQUEST = 'crowdspring/bumblebee/FETCH_AUTOMATION_REQUEST';
const FETCH_AUTOMATION_SUCCESS = 'crowdspring/bumblebee/FETCH_AUTOMATION_SUCCESS';

const SAVE_AUTOMATION = 'crowdspring/bumblebee/SAVE_AUTOMATION';
const SAVE_AUTOMATION_REQUEST = 'crowdspring/bumblebee/SAVE_AUTOMATION_REQUEST';
const SAVE_AUTOMATION_SUCCESS = 'crowdspring/bumblebee/SAVE_AUTOMATION_SUCCESS';
const SAVE_AUTOMATION_FAILURE = 'crowdspring/bumblebee/SAVE_AUTOMATION_FAILURE';

const FETCH_ENTRY_EVENTS = 'crowdspring/bumblebee/FETCH_ENTRY_EVENTS';
const FETCH_ENTRY_EVENTS_REQUEST = 'crowdspring/bumblebee/FETCH_ENTRY_EVENTS_REQUEST';
const FETCH_ENTRY_EVENTS_SUCCESS = 'crowdspring/bumblebee/FETCH_ENTRY_EVENTS_SUCCESS';

/* HELPERS
================================================================================================ */
const METRIC_LABELS = {
  unique_confirmed_opened: "opened",
  unique_clicked: "clicked",
  bounce: "bounces",
  spam_complaint: "spam reports"
};
const formatMetrics = (payload, period) => {
  const dates = rangeRight(period).map(n => moment().subtract(n, 'days'));
  const series = dates.map(date => {
    const dataForDate = Object.keys(payload).reduce((acc, metric) => ({
      ...acc,
      [METRIC_LABELS[metric] || metric]: payload[metric][date.format('YYYY-MM-DD')] || 0
    }), {});

    return ({
      date: date.format('MMM D'),
      ...dataForDate
    });
  });

  const totals = Object.keys(payload).reduce((acc, metric) => ({
    ...acc,
    [METRIC_LABELS[metric] || metric]: sum(Object.values(payload[metric])).toLocaleString()
  }), {});

  return { series, totals };
}

const fromAutomationEventToBreakdown = (slug) => {
  if (slug.startsWith("project-draft")) {
    return {
      action: "project-draft",
      countryTier: slug.split("-")[2],
      draftQualification: slug.split("-")[3],
      subCategory: slug.split("created-")[1],
    };
  }

  if (slug.startsWith("wrapup") && slug !== "wrapup-survey-crowdspring") {
    return {
      action: "wrapup",
      subCategory: slug.split("wrapup-")[1].split("-final-files-first-dl")[0],
    };
  }

  if (slug.startsWith("entry")) {
    return {
      action: "winner-awarded",
      subCategory: slug.split("entry-")[1].split("-awarded-winner")[0],
    };
  }

  return { "action": slug };
}

const fromBreakdownToAutomationEvent = (breakDown) => {
  const { action, countryTier, draftQualification, subCategory } = breakDown;

  if (action === "project-draft") {
    return `${action}-${countryTier}-${draftQualification}-created-${subCategory}`;
  }

  if (action === "wrapup") {
    return `${action}-${subCategory}-final-files-first-dl`;
  }

  if (action === "winner-awarded") {
    return `entry-${subCategory}-awarded-winner`;
  }

  return action;
}

const formatAutomation = (payload) => {
  return {
    ...payload,
    emails: sortBy(payload.emails.map(e => ({
      ...e,
      variations: sortBy(e.variations, "variation_number")
    })), "order"),
    entry_events: payload.entry_events.map(e => fromAutomationEventToBreakdown(e.slug)),
    entry_event_conditions: payload.entry_event_conditions.map(c => ({
      ...c,
      lead_query_parts: c.lead_query_parts.map(fromQueryPartToBreakdown)
    })),
    entry_attr_conditions: payload.entry_attr_conditions.map(c => ({
      ...c,
      lead_query_parts: c.lead_query_parts.map(fromQueryPartToBreakdown)
    }))
  };
}

export const cloneAutomationRequest = (id) => request.post(`/api/v1/bb/automations/${id}/clone/`);

export const deleteAutomationRequest = (id) => request.delete(`/api/v1/bb/automations/${id}/`);

export const createAutomationRequest = () => request.post('/api/v1/bb/automations/');

export const cloneGroupRequest = (id) => (
  request.post(`/api/v1/bb/automation_groups/${id}/clone/`)
);

export const deleteGroupRequest = (id) => (
  request.delete(`/api/v1/bb/automation_groups/${id}/`)
);

export const createGroupRequest = () => request.post('/api/v1/bb/automation_groups/');

export const fetchGroupRequest = (id) => request.get(`/api/v1/bb/automation_groups/${id}/`);

const saveEmailVariations = (current, updated, emailId) => {
  const toRemove = current.filter(v => !updated.find(v2 => v2.id === v.id));
  let premises = toRemove.reduce((acc, v) => (
    acc.then(() => request.delete(`/api/v1/bb/automation_email_variations/${v.id}/`))
  ), Promise.resolve());

  const toUpdate = updated.filter(v => current.find(v2 => v2.id === v.id));
  premises = toUpdate.reduce((acc, v) => (
    acc.then(() => request.patch(`/api/v1/bb/automation_email_variations/${v.id}/`, v))
  ), premises);

  const toAdd = updated.filter(v => !v.id);
  premises = toAdd.reduce((acc, v) => (
    acc.then(() => request.post("/api/v1/bb/automation_email_variations/", {
      ...v,
      automation_email: emailId
    }))
  ), premises);

  return premises;
}

const saveAutomationEmails = (current, updated, automationId) => {
  const toRemove = current.filter(e => !updated.find(e2 => e2.id === e.id));
  let premises = toRemove.reduce((acc, email) => (
    acc.then(() => request.delete(`/api/v1/bb/automation_emails/${email.id}/`))
  ), Promise.resolve());

  const toUpdate = updated.filter(e => current.find(e2 => e2.id === e.id));
  premises = toUpdate.reduce((acc, email) => {
    const currentEmail = current.find(e => e.id === email.id);
    const currentVariations = currentEmail.variations;
    const updatedVariations = email.variations;

    /* no need to perform further requests for this email if nothing has changed */
    if (isEqual(currentEmail, email)) {
      return acc;
    }

    return acc.then(
      () => request.patch(`/api/v1/bb/automation_emails/${email.id}/`, email).then(
      () => saveEmailVariations(currentVariations, updatedVariations, email.id)
    ));
  }, premises);

  const toAdd = updated.filter(e => !e.id);
  premises = toAdd.reduce((acc, email) => (
    acc.then(
      () => request.post("/api/v1/bb/automation_emails/", {
        ...email,
        automation: automationId
      }).then(
      (res) => saveEmailVariations([], email.variations, res.data.id)
    ))
  ), premises);

  return premises;
}

const saveAutomationEvents = (current, updated, automationId, allEvents) => {
  const getEvent = (slug) => allEvents.find(e => e.slug === slug);

  /* no need to perform further requests if nothing has changed */
  if (isEqual(current, updated)) {
    return Promise.resolve();
  }

  /* remove automation from current events */
  let promise = current.reduce((acc, slug) => {
    const event = getEvent(slug);
    return acc.then(() => request.patch(`/api/v1/bb/entry_events/${event.id}/`, {
      automations: event.automations.filter(a => a.id !== automationId).map(a => a.id)
    }));
  }, Promise.resolve());

  /* add automation to updated events */
  promise = updated.reduce((acc, slug) => {
    const event = getEvent(slug);
    return acc.then(() => request.patch(`/api/v1/bb/entry_events/${event.id}/`, {
      automations: union(event.automations.map(a => a.id), [automationId])
    }));
  }, promise);

  return promise;
}

/* INITIAL STATES
================================================================================================ */
const initialState = {
  totals: {},
  metrics: {},
  automations: [],
  automationCount: 0,
  isFetching: false,
  isFetchingSingle: false,
  isFetchingEntryEvents: false,
  groups: [],
  groupCount: 0,
  entryEvents: [],
  isSavingAutomation: false
};

/* REDUCERS
================================================================================================ */
export default function reducer(state = initialState, action) {
  let fieldsOfInterest;

  switch (action.type) {
    case FETCH_TOTALS_SUCCESS:
      return {
        ...state,
        totals: action.payload
      };
    case FETCH_METRICS_SUCCESS:
      return {
        ...state,
        metrics: formatMetrics(action.payload, action.period)
      };
    case FETCH_AUTOMATIONS_REQUEST:
    case FETCH_GROUPS_REQUEST:
      return {
        ...state,
        isFetching: true
      };
    case FETCH_GROUP_REQUEST:
    case FETCH_AUTOMATION_REQUEST:
      return {
        ...state,
        isFetchingSingle: true
      };
    case FETCH_AUTOMATIONS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        automations: action.payload.results,
        automationCount: action.payload.count
      };
    case FETCH_GROUP_SUCCESS:
      return {
        ...state,
        isFetchingSingle: false,
        group: action.payload
      };
    case PAUSE_AUTOMATION_SUCCESS:
    case RESUME_AUTOMATION_SUCCESS:
    case SET_LIVE_AUTOMATION_SUCCESS:
    case END_AUTOMATION_SUCCESS:
      fieldsOfInterest = pick(
        action.payload, 'id', 'status', 'last_action', 'last_action_at', 'last_action_by'
      );
      return {
        ...state,
        automations: state.automations.map(a => (
          a.id === fieldsOfInterest.id? { ...a, ...fieldsOfInterest } : a
        )),
        automation: (
          idx(state, _ => _.automation.id) === fieldsOfInterest.id?
          { ...state.automation, ...fieldsOfInterest } : state.automation
        )
      };
    case FETCH_GROUPS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        groups: action.payload.results,
        groupCount: action.payload.count
      };
    case PAUSE_GROUP_SUCCESS:
    case RESUME_GROUP_SUCCESS:
    case END_GROUP_SUCCESS:
      fieldsOfInterest = pick(
        action.payload, 'id', 'status', 'last_action', 'last_action_at', 'last_action_by'
      );
      return {
        ...state,
        groups: state.groups.map(g => (
          g.id === fieldsOfInterest.id? { ...g, ...fieldsOfInterest } : g
        )),
        group: (
          idx(state, _ => _.group.id) === fieldsOfInterest.id?
          { ...state.group, ...fieldsOfInterest } : state.group
        )
      };
    case FETCH_AUTOMATION_SUCCESS:
      return {
        ...state,
        isFetchingSingle: false,
        automation: formatAutomation(action.payload)
      };
    case FETCH_ENTRY_EVENTS_REQUEST:
      return {
        ...state,
        isFetchingEntryEvents: true
      };
    case FETCH_ENTRY_EVENTS_SUCCESS:
      return {
        ...state,
        isFetchingEntryEvents: false,
        entryEvents: action.payload
      };
    case SAVE_AUTOMATION_REQUEST:
      return {
        ...state,
        isSavingAutomation: true
      };
    case SAVE_AUTOMATION_FAILURE:
    case SAVE_AUTOMATION_SUCCESS:
      return {
        ...state,
        isSavingAutomation: false
      };
    default:
      return state;
  }
}

export const fetchTotals = (groupId) => (dispatch) => {
  const queryStr = queryString.stringify({ group_id: groupId });

  return dispatch({
    type: FETCH_TOTALS,
    promise: request.get(`/api/v1/bb/automations/counts_by_status/?${queryStr}`),
  });
};

export const fetchAutomations = (query, status, groupId) => (dispatch) => {
  const extraAPIQuery = {
    group: groupId,
    status: status === "all"? undefined : status
  };
  const queryStr = getAPIQueryString(AUTOMATIONS_RESOURCE_NAME, query, { extraAPIQuery });
  const promise = request.get(`/api/v1/bb/automations/?${queryStr}`);

  /* save page count in pager state */
  promise.then((res) => dispatch(savePageCount(AUTOMATIONS_RESOURCE_NAME, res)));

  return dispatch({
    type: FETCH_AUTOMATIONS,
    promise
  });
};

export const fetchGroup = (id) => (dispatch) => {
  return dispatch({
    type: FETCH_GROUP,
    promise: fetchGroupRequest(id)
  });
};

export const fetchMetrics = (period, groupId) => (dispatch) => {
  const queryStr = queryString.stringify({ period_days: period, group_id: groupId });

  return dispatch({
    type: FETCH_METRICS,
    promise: request.get(`/api/v1/bb/automation_groups/metrics/?${queryStr}`),
    period
  });
};

export const pauseAutomation = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automations/${id}/pause/`);

  dispatch({
    type: PAUSE_AUTOMATION,
    promise
  });

  return promise;
};

export const resumeAutomation = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automations/${id}/resume/`);

  dispatch({
    type: RESUME_AUTOMATION,
    promise
  });

  return promise;
};

export const setLiveAutomation = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automations/${id}/launch/`);

  promise.catch((err) => {
    const errorMsg = idx(err, _ => _.data.message) || 'Unknown error';
    dispatch(setBannerError('Error setting automation live', errorMsg));
  });

  dispatch({
    type: SET_LIVE_AUTOMATION,
    promise
  });

  return promise;
};

export const endAutomation = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automations/${id}/complete/`);

  dispatch({
    type: END_AUTOMATION,
    promise
  });

  return promise;
};

export const fetchGroups = (query) => (dispatch) => {
  const queryStr = getAPIQueryString(GROUPS_RESOURCE_NAME, query);
  const promise = request.get(`/api/v1/bb/automation_groups/?${queryStr}`);

  /* save page count in pager state */
  promise.then((res) => dispatch(savePageCount(GROUPS_RESOURCE_NAME, res)));

  return dispatch({
    type: FETCH_GROUPS,
    promise
  });
};

export const pauseGroup = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automation_groups/${id}/pause/`);

  dispatch({
    type: PAUSE_GROUP,
    promise
  });

  return promise;
};

export const resumeGroup = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automation_groups/${id}/resume/`);

  dispatch({
    type: RESUME_GROUP,
    promise
  });

  return promise;
};

export const endGroup = (id) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automation_groups/${id}/complete/`);

  dispatch({
    type: END_GROUP,
    promise
  });

  return promise;
};

export const saveGroup = (id, data) => (dispatch) => {
  const promise = request.patch(`/api/v1/bb/automation_groups/${id}/`, data);

  promise.then(() => {
    dispatch(
      showBanner('Success', 'Automation group successfully saved.', undefined, TYPE_SUCCESS)
    );
  }).catch((err) => {
    const errorMsg = idx(err, _ => _.data.message) || 'Make sure the data you entered is correct.';
    dispatch(setBannerError('Error saving automation group', errorMsg));
  });

  return dispatch({
    type: SAVE_GROUP,
    promise
  });
}

export const fetchAutomation = (id) => (dispatch) => {
  return dispatch({
    type: FETCH_AUTOMATION,
    promise: request.get(`/api/v1/bb/automations/${id}/`),
  });
};

export const sendTestEmail = (emailIdx, variationIdx, emails) => (dispatch, getState) => {
  const formValues = getState().form[AUTOMATION_FORM_NAME].values;
  const formData = {
    ...formValues,
    ...formValues.emails[emailIdx].variations[variationIdx]
  };
  return dispatch(sendTestEmailCommon(formData, emails));
}

export const saveAutomation = (id, showSuccessBanner=true) => (dispatch, getState) => {
  const state = getState();
  const { leadQueryParts } = state.emailAutomations.common;
  const { automation, entryEvents } = state.emailAutomations.bumblebee;
  const formData = state.form[AUTOMATION_FORM_NAME].values || {};
  const externalLeadTags = (formData.entry_external_lead_tags || "").trim();

  let automationData = {
    ...pick(
      formData, 'name', 'group', 'periodically_check_for_new_leads', 'unsubscribe_strategy',
      'exit_if_entry_condition_not_met', 'type', 'goal_1', 'goal_2', 'goal_3', 'goal_4',
      'limited'
    ),
    entry_external_lead_tags: externalLeadTags? [externalLeadTags] : [],
    extra_link_attributes: pick(formData, 'utm_source', 'utm_medium', 'utm_name'),
  };

  const currentEmails = automation.emails;
  const updatedEmails = formData.emails.map((email, i) => ({
    ...email,
    order: i + 1,
    variations: email.variations.map((variation, j) => ({
      ...omit(variation, 'html_data'),
      variation_number: j + 1,
      variation_allocation: ((1/email.variations.length)*100).toFixed(2),
      html_content: idx(variation, _ => _.html_data.html) || '',
      html_design: idx(variation, _ => _.html_data.design) || ''
    }))
  }));

  const promise = saveEntryConditions(
    automation.entry_attr_conditions, formData.entry_attr_conditions, leadQueryParts
  ).then((newAttrConditionIds) => {
    return saveEntryConditions(
      automation.entry_event_conditions, formData.entry_event_conditions, leadQueryParts
    ).then((newEventConditionIds) => {
      if (automation.status === "draft") {
        automationData = {
          ...automationData,
          entry_attr_conditions: newAttrConditionIds,
          entry_event_conditions: newEventConditionIds,
        };
      } else {
        /* only the name can be updated in non-draft automations */
        automationData = pick(automationData, "name");
      }

      return request.patch(`/api/v1/bb/automations/${id}/`, automationData);
    });
  })
  .then(() => saveAutomationEmails(currentEmails, updatedEmails, id))
  .then(() => saveAutomationEvents(
    automation.entry_events.map(fromBreakdownToAutomationEvent),
    formData.entry_events.map(fromBreakdownToAutomationEvent),
    parseInt(id),
    entryEvents
  ));

  promise.then(() => {
    if (showSuccessBanner) {
      dispatch(showBanner('Success', 'Automation successfully saved.', undefined, TYPE_SUCCESS));
    }
    /* reset form so that admin notes are cleared */
    dispatch(reset(AUTOMATION_FORM_NAME));
    dispatch(fetchAutomation(id));
  }).catch((err) => {
    const errorMsg = idx(err, _ => _.data.message) || 'Make sure the data you entered is correct.';
    dispatch(setBannerError('Error saving automation', errorMsg));
  });

  dispatch({
    type: SAVE_AUTOMATION,
    promise
  });

  return promise;
}

export const fetchEntryEvents = () => (dispatch) => {
  return dispatch({
    type: FETCH_ENTRY_EVENTS,
    promise: request.get("/api/v1/bb/entry_events/")
  });
}
